import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Chip,
  IconButton,
  Switch,
  Tooltip,
  CircularProgress,
  Snackbar,
  Alert,
  InputAdornment,
  MenuItem,
  Select,
  Grid,
  InputLabel,
  OutlinedInput,
} from "@mui/material";
import { Add, Edit, Password, Delete, Refresh, Search, FilterList, Clear } from "@mui/icons-material";
import { callApi } from "../../../utils/apiHelper";
import { apiEndpoints } from "../../../utils/apiEndpoints";
import { User } from "../../../utils/commonApis";

// Define role name mapping
const roleNames: { [key: number]: string } = {
  0: "Admin",
  1: "Manager",
  2: "Customer",
  3: "Vendor",
  4: "Warehouse",
  5: "Processing",
  6: "Security",
  7: "Cleaning",
  8: "Godown",
};

// Role options for the form - excluding Admin
const roleOptions = [
  { value: 1, label: "Manager" },
  { value: 2, label: "Customer" },
  { value: 3, label: "Vendor" },
  { value: 4, label: "Warehouse" },
  { value: 5, label: "Processing" },
  { value: 6, label: "Security" },
  { value: 7, label: "Cleaning" },
  { value: 8, label: "Godown" },
];

const UserManagement: React.FC = () => {
  // State variables
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [openUserForm, setOpenUserForm] = useState(false);
  const [openPasswordForm, setOpenPasswordForm] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [formMode, setFormMode] = useState<"create" | "edit">("create");
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState<number | "all">("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [alert, setAlert] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info" | "warning";
  }>({
    open: false,
    message: "",
    severity: "info",
  });

  // Form fields
  const [formData, setFormData] = useState({
    FullName: "",
    PhoneNumber: "",
    Role: "3", // Default to vendor
    password: "",
  });

  const [newPassword, setNewPassword] = useState("");

  // Fetch all users when component mounts
  useEffect(() => {
    fetchUsers();
  }, []);

  // Function to fetch all users from API (excluding admin users)
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await callApi<{ users: User[] }>(
        apiEndpoints.NulookErpManageUsers,
        "POST",
        {
          operation: "get_all",
        }
      );

      if (response.data?.users) {
        // Filter out admin users (role 0)
        const filteredUsers = response.data.users.filter(user => user.Role !== 0);
        setUsers(filteredUsers);
        setFilteredUsers(filteredUsers);
      } else {
        showAlert("Failed to load users", "error");
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      showAlert("Error loading users", "error");
    } finally {
      setLoading(false);
    }
  };

  // Function to handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Function to open form for creating a new user
  const handleAddUser = () => {
    setFormMode("create");
    setFormData({
      FullName: "",
      PhoneNumber: "",
      Role: "3", // Default to vendor
      password: "",
    });
    setOpenUserForm(true);
  };

  // Function to open form for editing a user
  const handleEditUser = (user: User) => {
    // Prevent editing admin users
    if (user.Role === 0) {
      showAlert("Admin users cannot be edited", "warning");
      return;
    }

    setFormMode("edit");
    setCurrentUser(user);
    setFormData({
      FullName: user.FullName,
      PhoneNumber: user.PhoneNumber.toString(),
      Role: user.Role.toString(),
      password: "",
    });
    setOpenUserForm(true);
  };

  // Function to open password change dialog
  const handlePasswordChange = (user: User) => {
    setCurrentUser(user);
    setNewPassword("");
    setOpenPasswordForm(true);
  };

  // Function to toggle user status (enable/disable)
  const handleToggleStatus = async (user: User) => {
    // Prevent toggling admin users
    if (user.Role === 0) {
      showAlert("Admin user status cannot be changed", "warning");
      return;
    }

    try {
      const response = await callApi(
        apiEndpoints.NulookErpManageUsers,
        "POST",
        {
          operation: "toggle_status",
          UserId: user.UserId,
          Status: !user.Status,
        },
      );

      if (response.data) {
        showAlert(
          `User ${!user.Status ? "enabled" : "disabled"} successfully`,
          "success",
        );
        fetchUsers();
      } else {
        showAlert("Failed to update user status", "error");
      }
    } catch (error) {
      console.error("Error toggling user status:", error);
      showAlert("Error updating user status", "error");
    }
  };

  // Function to submit user form (create or edit)
  const handleSubmitUserForm = async () => {
    // Form validation
    if (formMode === "create") {
      if (
        formData.FullName.trim() === "" ||
        formData.PhoneNumber.trim() === ""
      ) {
        showAlert("Please fill all required fields", "warning");
        return;
      }
    }

    try {
      if (formMode === "create") {
        const response = await callApi(
          apiEndpoints.NulookErpManageUsers,
          "POST",
          {
            operation: "create",
            FullName: formData.FullName,
            PhoneNumber: formData.PhoneNumber,
            Role: parseInt(formData.Role),
            password: formData.password,
            permission: [],
            subDepartmentIds: [],
          },
        );

        if (response.data) {
          showAlert("User created successfully", "success");
          setOpenUserForm(false);
          fetchUsers();
        } else {
          showAlert(
            response.error?.message || "Failed to create user",
            "error",
          );
        }
      } else if (formMode === "edit" && currentUser) {
        // Don't allow editing Admin users
        if (currentUser.Role === 0) {
          showAlert("Admin users cannot be edited", "error");
          setOpenUserForm(false);
          return;
        }

        const response = await callApi(
          apiEndpoints.NulookErpManageUsers,
          "POST",
          {
            operation: "update",
            UserId: currentUser.UserId,
            FullName: formData.FullName,
            Role: parseInt(formData.Role),
            Permissions: [],
            subDepartmentIds: [],
          },
        );

        if (response.data) {
          showAlert("User updated successfully", "success");
          setOpenUserForm(false);
          fetchUsers();
        } else {
          showAlert(
            response.error?.message || "Failed to update user",
            "error",
          );
        }
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      showAlert("Error processing your request", "error");
    }
  };

  // Function to submit password change
  const handleSubmitPasswordChange = async () => {
    if (!currentUser) return;

    try {
      const response = await callApi(
        apiEndpoints.NulookErpManageUsers,
        "POST",
        {
          operation: "change_password",
          UserId: currentUser.UserId,
          NewPassword: newPassword,
        },
      );

      if (response.data) {
        showAlert("Password changed successfully", "success");
        setOpenPasswordForm(false);
      } else {
        showAlert(
          response.error?.message || "Failed to change password",
          "error",
        );
      }
    } catch (error) {
      console.error("Error changing password:", error);
      showAlert("Error changing password", "error");
    }
  };

  // Function to show alerts
  const showAlert = (
    message: string,
    severity: "success" | "error" | "info" | "warning",
  ) => {
    setAlert({
      open: true,
      message,
      severity,
    });
  };

  // Function to close alert
  const handleCloseAlert = () => {
    setAlert({
      ...alert,
      open: false,
    });
  };

  // Apply filters to users
  useEffect(() => {
    let result = [...users];
    
    // Apply search term filter
    if (searchTerm) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      result = result.filter(
        user => 
          user.FullName.toLowerCase().includes(lowerSearchTerm) ||
          user.PhoneNumber.toString().includes(lowerSearchTerm) ||
          user.UserId.toLowerCase().includes(lowerSearchTerm)
      );
    }
    
    // Apply role filter
    if (roleFilter !== "all") {
      result = result.filter(user => user.Role === roleFilter);
    }
    
    // Apply status filter
    if (statusFilter !== "all") {
      result = result.filter(user => 
        statusFilter === "active" ? user.Status === true : user.Status === false
      );
    }
    
    setFilteredUsers(result);
  }, [users, searchTerm, roleFilter, statusFilter]);

  // Handle search input changes
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm("");
    setRoleFilter("all");
    setStatusFilter("all");
  };

  // Format timestamp to readable date
  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleString();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h5">User Management</Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchUsers}
            sx={{ mr: 1 }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAddUser}
          >
            Add User
          </Button>
        </Box>
      </Box>

      {/* Search and filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              placeholder="Search by name or phone..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm("")}>
                      <Clear fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="role-filter-label">Role</InputLabel>
              <Select
                labelId="role-filter-label"
                value={roleFilter}
                label="Role"
                onChange={(e) => setRoleFilter(e.target.value as number | "all")}
              >
                <MenuItem value="all">All Roles</MenuItem>
                {roleOptions.map((role) => (
                  <MenuItem key={role.value} value={role.value}>
                    {role.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="status-filter-label">Status</InputLabel>
              <Select
                labelId="status-filter-label"
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button 
              fullWidth 
              variant="outlined"
              startIcon={<FilterList />}
              onClick={clearFilters}
              disabled={searchTerm === "" && roleFilter === "all" && statusFilter === "all"}
            >
              Clear
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <Box p={2} display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="body2">
            {loading ? 'Loading users...' : `Showing ${filteredUsers.length} of ${users.length} users`}
          </Typography>
        </Box>
        <TableContainer sx={{ maxHeight: "calc(100vh - 350px)" }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>

                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress sx={{ my: 3 }} />
                  </TableCell>
                </TableRow>
              ) : filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body1" sx={{ py: 2 }}>
                      No users found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((user) => (
                  <TableRow key={user.UserId}>
                    <TableCell>{user.FullName}</TableCell>
                    <TableCell>{user.PhoneNumber}</TableCell>
                    <TableCell>
                      <Chip
                        label={roleNames[user.Role] || `Role ${user.Role}`}
                        color={user.Role === 0 ? "primary" : "default"}
                        variant={user.Role === 0 ? "filled" : "outlined"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip
                        title={user.Status ? "Active" : "Disabled"}
                        arrow
                      >
                        <Switch
                          checked={user.Status}
                          onChange={() => handleToggleStatus(user)}
                          color="primary"
                          size="small"
                        />
                      </Tooltip>
                    </TableCell>

                    <TableCell align="center">
                      <Box>
                        <Tooltip title="Edit User">
                          <IconButton
                            color="primary"
                            onClick={() => handleEditUser(user)}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Change Password">
                          <IconButton
                            color="primary"
                            onClick={() => handlePasswordChange(user)}
                          >
                            <Password />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* User Form Dialog */}
      <Dialog
        open={openUserForm}
        onClose={() => setOpenUserForm(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {formMode === "create" ? "Add New User" : "Edit User"}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <TextField
              label="Full Name"
              name="FullName"
              value={formData.FullName}
              onChange={handleInputChange}
              fullWidth
              required
            />
            <TextField
              label="Phone Number"
              name="PhoneNumber"
              value={formData.PhoneNumber}
              onChange={handleInputChange}
              fullWidth
              required
              disabled={formMode === "edit"}
              helperText={
                formMode === "edit"
                  ? "Phone number cannot be changed after creation"
                  : ""
              }
            />
            <FormControl component="fieldset">
              <FormLabel component="legend">Role</FormLabel>
              <RadioGroup
                row
                name="Role"
                value={formData.Role}
                onChange={handleInputChange}
              >
                {roleOptions.map((role) => (
                  <FormControlLabel
                    key={role.value}
                    value={role.value.toString()}
                    control={<Radio />}
                    label={role.label}
                  />
                ))}
              </RadioGroup>
            </FormControl>
            {formMode === "create" && (
              <TextField
                label="Password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleInputChange}
                fullWidth
                required
              />
            )}
            {/* Additional fields can be added here if needed */}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenUserForm(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmitUserForm}
            disabled={
              formMode === "create" &&
              (formData.FullName.trim() === "" ||
                formData.PhoneNumber.trim() === "")
            }
          >
            {formMode === "create" ? "Create" : "Update"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Password Change Dialog */}
      <Dialog
        open={openPasswordForm}
        onClose={() => setOpenPasswordForm(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" gutterBottom>
              Changing password for:{" "}
              <strong>{currentUser?.FullName || ""}</strong>
            </Typography>
            <TextField
              label="New Password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              fullWidth
              required
              margin="normal"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPasswordForm(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmitPasswordChange}
            disabled={!newPassword}
          >
            Change Password
          </Button>
        </DialogActions>
      </Dialog>

      {/* Alert Snackbar */}
      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseAlert}
          severity={alert.severity}
          sx={{ width: "100%" }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserManagement;
